import { useMemo, useState } from "react";
import { buildHisab } from "./campus_hisab_calc";
import {
    Chips, Stat, fmtDay, fmtINR, fmtNum, scopeLabel, useQrPayments,
} from "./campus_hisab_ui";

const STATUS = {
    matched: { label: "Tied to bill", tone: "ok" },
    "zero-amount": { label: "No amount saved", tone: "warn" },
    "cash-conflict": { label: "Bill says cash", tone: "warn" },
    extra: { label: "Extra on bill", tone: "warn" },
    "no-such-bill": { label: "Bill not found", tone: "bad" },
    loose: { label: "No bill", tone: "bad" },
};

const STATUS_FILTERS = [
    { id: "", label: "All" },
    { id: "matched", label: "Tied to bill" },
    { id: "problem", label: "Needs checking" },
];

export default function CampusQrPay({ bookings = [], drive = {} }) {
    const { rows: qrRows, state, at, refresh } = useQrPayments();
    const [scope, setScope] = useState("drive");
    const [qrFilter, setQrFilter] = useState("");
    const [staffFilter, setStaffFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [search, setSearch] = useState("");

    const driveOn = !!(drive.campus || drive.from || drive.to);
    const activeDrive = useMemo(
        () => (scope === "drive" && driveOn ? drive : {}),
        [scope, driveOn, drive]
    );

    const hisab = useMemo(() => buildHisab(bookings, qrRows, activeDrive), [bookings, qrRows, activeDrive]);
    const entries = hisab.qrEntries;

    const qrNames = useMemo(() => [...new Set(entries.map((q) => q.qrName).filter(Boolean))].sort(), [entries]);
    const staffNames = useMemo(() => [...new Set(entries.map((q) => q.staffName).filter(Boolean))].sort(), [entries]);
    const days = useMemo(() => [...new Set(entries.map((q) => q.day).filter(Boolean))].sort(), [entries]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return entries.filter((r) => {
            if (qrFilter && r.qrName !== qrFilter) return false;
            if (staffFilter && r.staffName !== staffFilter) return false;
            if (statusFilter === "matched" && r.status !== "matched") return false;
            if (statusFilter === "problem" && r.status === "matched") return false;
            if (!q) return true;
            return [r.bill, r.customer, r.qrName, r.staffName, r.matchedInvoice]
                .filter(Boolean).join(" ").toLowerCase().includes(q);
        });
    }, [entries, qrFilter, staffFilter, statusFilter, search]);

    const shown = useMemo(() => {
        const total = filtered.reduce((a, r) => a + r.amount, 0);
        const matched = filtered.filter((r) => r.status === "matched");
        const problems = filtered.filter((r) => r.status !== "matched");
        return {
            total,
            matched: matched.reduce((a, r) => a + r.amount, 0),
            matchedCount: matched.length,
            problemCount: problems.length,
            problemAmount: problems.reduce((a, r) => a + r.amount, 0),
        };
    }, [filtered]);

    const staffRows = useMemo(
        () => hisab.people.filter((p) => p.qrCount > 0 || p.online > 0),
        [hisab.people]
    );

    if (state === "loading" && !entries.length) {
        return <div className="campus-orders-empty">Loading QR payments…</div>;
    }

    return (
        <div className="campus-panel campus-hisab">
            <div className="campus-hisab-head">
                <div>
                    <div className="campus-table-title">
                        QR payments — {scope === "drive" && driveOn ? scopeLabel(drive) : "everything in the log"}
                    </div>
                    <p className="campus-hisab-lead">
                        A row lands here only when Done is pressed in the app. Each row is checked against the bill it belongs to,
                        so you can see which QR received the money and which payments are still unmatched.
                        {at && ` Loaded ${new Date(at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}.`}
                    </p>
                </div>
                <button type="button" className="campus-orders-refresh" onClick={refresh} disabled={state === "loading"}>
                    {state === "loading" ? "Loading…" : "↻ Refresh"}
                </button>
            </div>

            {state === "error" && <p className="campus-hisab-warn">QR list could not be loaded. Try Refresh.</p>}
            {hisab.qrLoaded >= 500 && hisab.qrOldest && (
                <p className="campus-hisab-warn">
                    The log returns the latest 500 payments only — currently back to {fmtDay(hisab.qrOldest)}.
                    Anything older is not in this screen.
                </p>
            )}

            {driveOn && (
                <div className="campus-hisab-nav">
                    <Chips
                        options={[
                            { id: "drive", label: scopeLabel(drive) },
                            { id: "all", label: "All QR in log", count: hisab.qrLoaded },
                        ]}
                        value={scope}
                        onChange={setScope}
                    />
                </div>
            )}

            <div className="campus-stat-row">
                <Stat label="On QR" value={fmtINR(hisab.totals.qr)} sub={`${fmtNum(hisab.totals.qrCount)} payments`} tone="accent" />
                <Stat label="Tied to a bill" value={fmtINR(hisab.totals.matchedQr)} tone="good" />
                <Stat label="QRs used" value={fmtNum(hisab.qrAccounts.length)} />
                <Stat
                    label="Needs checking"
                    value={fmtNum(entries.filter((q) => q.status !== "matched").length)}
                    sub="Rows below in red or amber"
                    tone={entries.some((q) => q.status !== "matched") ? "bad" : "good"}
                />
                <Stat
                    label="Online with no QR"
                    value={fmtINR(hisab.totals.short)}
                    sub={`${fmtNum(hisab.totals.shortBills)} bills — Done not pressed`}
                    tone={hisab.totals.short > 0 ? "bad" : "good"}
                />
            </div>

            <div className="campus-table-title">Collected per QR</div>
            <div className="campus-qr-cards">
                {hisab.qrAccounts.length ? hisab.qrAccounts.map((a) => (
                    <button
                        type="button"
                        key={a.name}
                        className={`campus-qr-card campus-qr-card--btn ${qrFilter === a.name ? "is-on" : ""}`}
                        onClick={() => setQrFilter(qrFilter === a.name ? "" : a.name)}
                    >
                        <strong>{a.name}</strong>
                        <span className="campus-qr-amt">{fmtINR(a.total)}</span>
                        <span className="campus-qr-cnt">{fmtNum(a.count)} payment{a.count > 1 ? "s" : ""}</span>
                        <span className="campus-qr-cnt">Done by {a.staff.join(", ") || "—"}</span>
                    </button>
                )) : <div className="campus-orders-empty">No QR payments here.</div>}
            </div>
            <p className="campus-note">Click a QR to filter the log below. “Done by” is the staff phone that pressed Done, not always the QR owner.</p>

            {days.length > 1 && (
                <>
                    <div className="campus-table-title">QR by day</div>
                    <div className="campus-table-wrap">
                        <table className="campus-table">
                            <thead>
                                <tr>
                                    <th>QR</th>
                                    {days.map((d) => <th key={d} className="r">{fmtDay(d)}</th>)}
                                    <th className="r">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {hisab.qrAccounts.map((a) => {
                                    const map = Object.fromEntries(a.days.map((d) => [d.day, d]));
                                    return (
                                        <tr key={a.name}>
                                            <td><strong>{a.name}</strong></td>
                                            {days.map((d) => (
                                                <td key={d} className="r">
                                                    {map[d] ? fmtINR(map[d].total) : "—"}
                                                    {map[d] && <div className="campus-hisab-sub is-muted">{fmtNum(map[d].count)}</div>}
                                                </td>
                                            ))}
                                            <td className="r"><strong>{fmtINR(a.total)}</strong></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td><strong>Total</strong></td>
                                    {days.map((d) => {
                                        const sum = entries.filter((q) => q.day === d).reduce((x, q) => x + q.amount, 0);
                                        return <td key={d} className="r"><strong>{fmtINR(sum)}</strong></td>;
                                    })}
                                    <td className="r"><strong>{fmtINR(hisab.totals.qr)}</strong></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </>
            )}

            <div className="campus-table-title">Who pressed Done</div>
            <div className="campus-table-wrap">
                <table className="campus-table">
                    <thead>
                        <tr>
                            <th>Staff</th>
                            <th className="r">Done pressed</th>
                            <th className="r">QR amount</th>
                            <th className="r">Online they collected</th>
                            <th className="r">Not on a QR</th>
                        </tr>
                    </thead>
                    <tbody>
                        {staffRows.length ? staffRows.map((p) => (
                            <tr key={p.name} className={p.short > 0 ? "campus-row-short" : ""}>
                                <td><strong>{p.name}</strong></td>
                                <td className="r">{p.qrCount || "—"}</td>
                                <td className="r">{p.qr ? fmtINR(p.qr) : "—"}</td>
                                <td className="r">{p.online ? fmtINR(p.online) : "—"}</td>
                                <td className="r">{p.short > 0 ? <strong className="campus-hisab-short">{fmtINR(p.short)}</strong> : "—"}</td>
                            </tr>
                        )) : <tr><td colSpan={5} className="c">No staff activity here.</td></tr>}
                    </tbody>
                </table>
            </div>
            <p className="campus-note">QR amount can be more than the online a person collected — a customer may scan a QR in one name while another person presses Done.</p>

            <div className="campus-table-title">Every QR payment</div>
            <div className="campus-orders-toolbar campus-dist-toolbar">
                <Chips options={STATUS_FILTERS} value={statusFilter} onChange={setStatusFilter} />
                <div className="campus-orders-search">
                    <input
                        type="search"
                        placeholder="Bill, customer, QR, staff…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        autoComplete="off"
                    />
                </div>
                <select className="campus-orders-select" value={qrFilter} onChange={(e) => setQrFilter(e.target.value)}>
                    <option value="">All QRs</option>
                    {qrNames.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <select className="campus-orders-select" value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)}>
                    <option value="">All staff</option>
                    {staffNames.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
            </div>

            <div className="campus-subnote">
                Showing <strong>{fmtNum(filtered.length)}</strong> payments · <strong>{fmtINR(shown.total)}</strong>
                {" · tied to a bill "}<strong>{fmtINR(shown.matched)}</strong> ({fmtNum(shown.matchedCount)})
                {shown.problemCount > 0 && <> · needs checking <strong>{fmtNum(shown.problemCount)}</strong> ({fmtINR(shown.problemAmount)})</>}
            </div>

            <div className="campus-table-wrap">
                <table className="campus-table">
                    <thead>
                        <tr>
                            <th>When</th>
                            <th>QR</th>
                            <th>Bill</th>
                            <th>Customer</th>
                            <th>Done by</th>
                            <th className="r">Amount</th>
                            <th>Match</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length ? filtered.map((r, i) => {
                            const st = STATUS[r.status] || STATUS.loose;
                            return (
                                <tr key={`${r.at}-${i}`} className={st.tone === "bad" ? "campus-row-short" : ""}>
                                    <td>{fmtDay(r.day)}<div className="campus-hisab-sub is-muted">{r.clock}</div></td>
                                    <td><strong>{r.qrName || "—"}</strong></td>
                                    <td>{r.bill || (r.matchedInvoice ? <span className="campus-hisab-sub is-muted">{r.matchedInvoice} (by name)</span> : "—")}</td>
                                    <td>{r.customer || "—"}</td>
                                    <td>{r.staffName || "—"}</td>
                                    <td className="r"><strong>{r.amount > 0 ? fmtINR(r.amount) : "—"}</strong></td>
                                    <td><span className={`campus-hisab-badge is-${st.tone}`}>{st.label}</span></td>
                                </tr>
                            );
                        }) : <tr><td colSpan={7} className="c">No payments match.</td></tr>}
                    </tbody>
                </table>
            </div>
            <p className="campus-note">
                Tied to bill means the QR amount matches a payment on that bill. No amount saved means Done went through but the amount came as zero.
                Bill says cash means the same amount is marked cash in the app — check the mode before counting cash.
                Full per-bill detail is in Payments → Bill by bill.
            </p>
        </div>
    );
}
